import styles from './ServiceShareFilters.module.scss';
import { Control, Controller, useWatch } from 'react-hook-form';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import {
	IServiceShareFiltersModel,
	IServiceShareInputFiltersOptions
} from '../../../models/serviceShareInput/ServiceShareFiltersModel';
import { AddServiceShareInput } from '../add/AddServiceShareInput';
import { useAddServiceShareInput } from '../add/hooks/useAddServiceShareInput';
import { useCookies } from 'react-cookie';
import { Button } from 'primereact/button';
import { Calendar, CalendarChangeParams } from 'primereact/calendar';
import { AutoComplete, AutoCompleteCompleteMethodParams } from "primereact/autocomplete";
import './Overrides.scss';

export interface IFiltersProps {
	control: Control<IServiceShareFiltersModel>;
	triggerRefetch: () => void;
	clearFilters: () => void;
	searchDistributors: (e: AutoCompleteCompleteMethodParams) => void
}

export const Filters = ({
	control,
	triggerRefetch,
	clearFilters,
	searchDistributors
}: IFiltersProps) => {
	const filtersValues = useWatch<IServiceShareFiltersModel>({
		control: control
	});

	const {
		control: addServiceShareInputControl,
		isInputDialogVisible,
		onDialogVisibleChange,
		isLoading,
		handleSubmitRequest,
	} = useAddServiceShareInput(triggerRefetch);

	const [cookies, setCookie] = useCookies([
		'serviceShareInputQuarter',
		'serviceShareInputYear',
		'serviceShareInputBrands',
		'serviceShareInputChannel',
		'serviceShareInputCountry'
	]);

	return (
		<div className={styles.container}>
			<Controller
				name='filterValuesSelected.quarterlyPeriodYear'
				control={control}
				render={({ field, fieldState }) => (
					<Calendar
						id={field.name}
						{...field}
						value={field.value ? new Date(field.value, 0) : undefined}
						className={`${styles.filter} ${styles.calendarFilter}`}
						placeholder='Year'
						onChange={(e: CalendarChangeParams) => {
							const year = (e.value as Date).getFullYear();

							if(year){
								field.onChange(year);
								setCookie('serviceShareInputYear', year);
							}
						}}
						view='year'
						dateFormat='yy'
						panelClassName={styles.calendarFilterPanel}
						appendTo='self'
					/>
				)}
			/>
			<Controller
				name='filterValuesSelected.quarterlyPeriodQuarter'
				control={control}
				render={({ field, fieldState }) => (
					<Dropdown
						id={field.name}
						{...field}
						className={styles.filter}
						placeholder='Quarter'
						options={
							filtersValues.filterValuesOptions?.quartersOptions
						}
						onChange={(e) => {
							field.onChange(e);
							setCookie(`serviceShareInputQuarter`, e.value);
						}}
					/>
				)}
			/>
			<Controller
				name={'filterValuesSelected.distributorOrEntity'}
				control={control}
				render={({ field, fieldState }) => (
					<AutoComplete
						id={field.name}
						{...field}
						className={styles.autoComplete}
						suggestions={filtersValues.filterValuesOptions?.selectedDistributorsOptions} 
						completeMethod={searchDistributors} 
						onChange={(e) => field.onChange(e.value)}
						placeholder='Distributor/Entity'
					/>
				)}
			/>
			<Controller
				name='filterValuesSelected.brandsIds'
				control={control}
				render={({ field, fieldState }) => (
					<MultiSelect
						id={field.name}
						filter
						{...field}
						className={styles.filter}
						placeholder='Brands'
						options={
							filtersValues.filterValuesOptions?.brandsOptions
						}
						onChange={(e) => {
							field.onChange(e);
							setCookie(`serviceShareInputBrands`, e.value);
						}}
					/>
				)}
			/>
			<Controller
				name='filterValuesSelected.channel'
				control={control}
				render={({ field, fieldState }) => (
					<Dropdown
						id={field.name}
						{...field}
						className={styles.filter}
						placeholder='Channel'
						options={
							filtersValues.filterValuesOptions?.channelsOptions
						}
						onChange={(e) => {
							field.onChange(e);
							setCookie(`serviceShareInputChannel`, e.value);
						}}
					/>
				)}
			/>
			<Controller
				name='filterValuesSelected.countryId'
				control={control}
				render={({ field, fieldState }) => (
					<Dropdown
						id={field.name}
						filter
						{...field}
						className={styles.filter}
						placeholder='Country'
						options={
							filtersValues.filterValuesOptions?.countriesOptions
						}
						onChange={(e) => {
							field.onChange(e);
							setCookie(`serviceShareInputCountry`, e.value);
						}}
					/>
				)}
			/>

			<Button className={styles.filterClearBtn} onClick={clearFilters}>
				<img src='/assets/icon-trash.svg' alt='trash icon' />
				Clear all filters
			</Button>

			<AddServiceShareInput
				control={addServiceShareInputControl}
				dropdownsOptions={
					filtersValues.filterValuesOptions as IServiceShareInputFiltersOptions
				}
				isInputDialogVisible={isInputDialogVisible}
				onDialogVisibleChange={onDialogVisibleChange}
				isLoading={isLoading}
				handleSubmitRequest={handleSubmitRequest}
			/>
		</div>
	);
};
